import * as Sentry from '@sentry/browser';
import Z from 'vendor/zuora';
import axios from '~/javascripts/lib/axios_utils';
import {
  subscribeToZuoraClientSideErrorEvent,
  subscribeToZuoraErrorEvent,
  subscribeToZuoraSuccessEvent,
  unsubscribeFromZuoraClientSideErrorEvent,
  unsubscribeFromZuoraErrorEvent,
  unsubscribeFromZuoraSuccessEvent,
} from '~/javascripts/lib/zuora_utils';
import { NEW_CREDIT_CARD_VALUE } from '~/javascripts/purchase/constants';
import {
  AUTORENEW_SUBSCRIPTION_URL,
  CHANGE_SUBSCRIPTION_URL,
  ORDERS_URL,
  PAYMENT_METHODS_URL,
  PREVIEW_UPGRADE_SUBSCRIPTION_URL,
  RENEW_SUBSCRIPTION_URL,
  UPGRADE_SUBSCRIPTION_URL,
} from '~/javascripts/subscription/constants';
import { ERROR_CODE_REQUEST_CANCELED } from '~/javascripts/constants';
import {
  getPaymentErrorFromResponse,
  getSubscriptionCreateOrUpdatePreviewResponse,
  getSubscriptionUpgradeResponse,
} from './parse_responses';

/**
 * This hits the create endpoint in app/controllers/subscriptions_controller.rb
 * This endpoint also support a "preview" flag, which duplicates the logic here.
 */
function axiosPostSubscription(params, abortSignal) {
  return axios.post('/subscriptions', params, { signal: abortSignal });
}

/**
 * This hits the update endpoint in app/controllers/subscriptions_controller.rb
 * This endpoint also support a "preview" flag, which duplicates the logic here.
 */
function axiosPutSubscription(params, abortSignal) {
  return axios.put(`/subscriptions/${params.subscription_id}`, params, { signal: abortSignal });
}

function handleCreateOrUpdateSubscription(requestFn, params, abortSignal) {
  return requestFn(params, abortSignal)
    .then(({ data }) => {
      if (params.preview) {
        return getSubscriptionCreateOrUpdatePreviewResponse(data);
      }
      if (gon.global.jh) {
        // jh override here
        window.location.href = data?.pay_url || data.invoice_data.invoice.pay_url;
        return data;
      }

      // Send payment_method_id in the response object to capture it when tracking purchase
      // transactions on Google Analytics. New payment_method_id is created just before making a
      // purchase request when customer chooses to setup a new credit card. paument_method_id
      // will have the id of an existing payment method when choosing to use an existing card
      return { ...data, payment_method_id: params.payment_method_id };
    })
    .catch((error) => {
      if (error.code === ERROR_CODE_REQUEST_CANCELED) {
        throw error;
      }
      Sentry.captureException(error);
      throw getPaymentErrorFromResponse(error.response?.data);
    });
}

/**
 * In case of addon subscriptions, we always call `post /subscriptions` endpoint
 * and backend decides whether to amend existing subscription or create a new one.
 */
export function createOrUpdateAddonSubscription(params, abortSignal) {
  return handleCreateOrUpdateSubscription(axiosPostSubscription, params, abortSignal);
}

export function createOrUpdateSubscription(params, abortSignal) {
  const requestFn = params.subscription_id ? axiosPutSubscription : axiosPostSubscription;

  return handleCreateOrUpdateSubscription(requestFn, params, abortSignal);
}

export function autoRenewSubscription(subscriptionName, shouldRenew) {
  return axios
    .put(
      AUTORENEW_SUBSCRIPTION_URL.replace(
        ':subscription_name',
        encodeURIComponent(subscriptionName),
      ),
      {
        auto_renew: shouldRenew,
      },
    )
    .then(({ data }) => {
      if (data.success !== true) {
        throw data.errors;
      }

      return data;
    })
    .catch((error) => {
      Sentry.captureException(error);
      throw error;
    });
}

/**
 * Since we're submitting a credit card to our payment provider
 * we need to wait for a webhook callback in
 * app/views/zuora_callbacks/payment_method.html.haml
 * and listen for the event here, to make it look like 1 request to the user
 */
export async function setupNewCreditCard() {
  return new Promise((resolve, reject) => {
    function successCallback(event) {
      resolve(event.detail);
    }

    function errorCallback(event) {
      unsubscribeFromZuoraErrorEvent(errorCallback);
      unsubscribeFromZuoraClientSideErrorEvent(errorCallback);
      unsubscribeFromZuoraSuccessEvent(successCallback);
      const { message, cause } = getPaymentErrorFromResponse(event.detail);
      reject(new Error(message, { cause }));
    }

    subscribeToZuoraSuccessEvent(successCallback);
    subscribeToZuoraErrorEvent(errorCallback);
    subscribeToZuoraClientSideErrorEvent(errorCallback);

    // Kick off the CC submit to Zuora and wait for custom events to trigger
    Z.submit();
  });
}

function handlePurchaseSubscription(createOrUpdateFn, params, abortSignal) {
  const isPurchaseWithNewCreditCard =
    !params.preview && params.payment_method_id === NEW_CREDIT_CARD_VALUE;

  if (isPurchaseWithNewCreditCard) {
    return setupNewCreditCard().then(({ paymentMethodId }) => {
      return createOrUpdateFn(
        {
          ...params,
          payment_method_id: paymentMethodId,
        },
        abortSignal,
      );
    });
  }
  return createOrUpdateFn(params, abortSignal);
}

export function purchaseAddonSubscription(params, abortSignal) {
  return handlePurchaseSubscription(createOrUpdateAddonSubscription, params, abortSignal);
}

export function purchaseSubscription(params, abortSignal) {
  return handlePurchaseSubscription(createOrUpdateSubscription, params, abortSignal);
}

const paramNameMap = {
  inlineSubmit: 'enable_submit',
  invoiceNumber: 'invoice_number',
};

export function fetchZuoraParams(flow = 'cc', options = {}) {
  const params = Object.keys(paramNameMap).reduce((memo, key) => {
    const paramName = paramNameMap[key];
    const value = options[key];
    // Only include keys present in the options
    if (value == null) {
      return memo;
    }

    return [...memo, `${paramName}=${value}`];
  }, []);

  const path = `/payment_forms/${flow}?${params.join('&')}`;

  return axios.get(path).then(({ data }) => data);
}

export function previewUpgradeSubscription(subscriptionName, planId) {
  const url = PREVIEW_UPGRADE_SUBSCRIPTION_URL.replace(
    ':subscription_name',
    encodeURIComponent(subscriptionName),
  ).replace(':plan_id', encodeURIComponent(planId));

  return axios
    .get(url)
    .then(({ data }) => getSubscriptionUpgradeResponse(data))
    .catch((error) => {
      Sentry.captureException(error);
      throw getPaymentErrorFromResponse(error.response?.data);
    });
}

export function upgradeSubscription(subscriptionName, params) {
  const path = UPGRADE_SUBSCRIPTION_URL.replace(
    ':subscription_name',
    encodeURIComponent(subscriptionName),
  );

  return axios
    .post(path, params)
    .then(({ data }) => data)
    .catch((error) => {
      Sentry.captureException(error);
      throw getPaymentErrorFromResponse(error.response?.data);
    });
}

export function purchaseUpgradeSubscription(subscriptionName, params) {
  if (params.payment_method_id === NEW_CREDIT_CARD_VALUE) {
    return setupNewCreditCard().then(({ paymentMethodId }) => {
      return upgradeSubscription(subscriptionName, {
        ...params,
        payment_method_id: paymentMethodId,
      });
    });
  }
  return upgradeSubscription(subscriptionName, params);
}

export function changeNamespace(orderId, requestParams) {
  return axios
    .put(ORDERS_URL.replace(':order_id', encodeURIComponent(orderId)), requestParams)
    .catch((error) => {
      throw error?.response?.data?.errors || error;
    });
}

export async function addNewCreditCard() {
  const { paymentMethodId } = await setupNewCreditCard();
  try {
    return await axios.put(
      PAYMENT_METHODS_URL.replace(':payment_method_id', encodeURIComponent(paymentMethodId)),
    );
  } catch (error) {
    Sentry.captureException(error);
    throw getPaymentErrorFromResponse(error.response?.data);
  }
}

export async function changeSubscription(subscriptionName, requestParams = {}, abortSignal) {
  return axios
    .put(
      CHANGE_SUBSCRIPTION_URL.replace(':subscription_name', encodeURIComponent(subscriptionName)),
      requestParams,
      {
        signal: abortSignal,
      },
    )
    .then(({ data }) => data)
    .catch((error) => {
      if (error.code === ERROR_CODE_REQUEST_CANCELED) {
        throw error;
      }
      Sentry.captureException(error);
      throw getPaymentErrorFromResponse(error.response?.data);
    });
}

/**
 *
 * @params {Object} params
 * create,update,destroy, change state about receipt
 */

export function createReceipt(params) {
  return axios.post('/receipts.json', params);
}

export function cancelReceipt(id) {
  return axios.post(`/receipts/${id}/cancel.json`);
}

export function fetchChinaProvince() {
  return axios.get(`/api/countries/CHN/states.json`).then(({ data }) => data);
}

export function fetchChinaCities() {
  return axios.get(`/api/countries/china_cities.json`).then(({ data }) => data);
}

/**
 *
 * @params {Object} params
 * create, remove about promotion codes
 */

export function applyPromotion(params = { invoiceId: '', promo_code: '' }) {
  return axios.post(`/invoices/${params.invoiceId}/promotions.json`, params);
}

export function removePromotion(params = { invoiceId: '', promo_code: '' }) {
  return axios.post(`/invoices/${params.invoiceId}/promotions/remove.json`, params);
}

/**
 *
 * @param {String} subscriptionName
 * @param {Object} params
 * @param {AbortSignal} abortSignal
 * Wraps changeSubscription in `setupNewCreditCard` call
 * which is required for all flows where the user hits
 * the Zuora iframe
 * The AbortSignal can be used to abort the heavier `changeSubscription` request.
 * https://developer.mozilla.org/en-US/docs/Web/API/AbortController
 */

export function purchaseChangeSubscription(subscriptionName, params, abortSignal) {
  const isPurchaseWithNewCreditCard =
    !params.preview && params.payment_method_id === NEW_CREDIT_CARD_VALUE;

  if (isPurchaseWithNewCreditCard) {
    return setupNewCreditCard().then(({ paymentMethodId }) => {
      return changeSubscription(
        subscriptionName,
        {
          ...params,
          payment_method_id: paymentMethodId,
        },
        abortSignal,
      );
    });
  }

  return changeSubscription(subscriptionName, params, abortSignal);
}

function callRenewSubscriptionApi(subscriptionName, params, abortSignal) {
  return axios
    .put(
      RENEW_SUBSCRIPTION_URL.replace(':subscription_name', encodeURIComponent(subscriptionName)),
      params,
      {
        signal: abortSignal,
      },
    )
    .then(({ data }) => {
      if (data.success !== true) {
        throw data.errors;
      }
      return data;
    })
    .catch((error) => {
      if (error.code === ERROR_CODE_REQUEST_CANCELED) {
        throw error;
      }
      Sentry.captureException(error);
      throw getPaymentErrorFromResponse(error.response?.data);
    });
}

export function renewSubscription(subscriptionName, params, abortSignal) {
  const isPurchaseWithNewCreditCard =
    !params.preview && params.payment_method_id === NEW_CREDIT_CARD_VALUE;

  if (isPurchaseWithNewCreditCard) {
    return setupNewCreditCard().then(({ paymentMethodId }) => {
      return callRenewSubscriptionApi(
        subscriptionName,
        {
          ...params,
          payment_method_id: paymentMethodId,
        },
        abortSignal,
      );
    });
  }
  return callRenewSubscriptionApi(subscriptionName, params, abortSignal);
}
